import * as urls from "../../../api/urls";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Tabs, Tab } from "react-bootstrap";
import { Utils } from "helpers/utils";
import { APIClient } from "api/apiCore";
import Loader from "components/Loader";
import MasterPage1Column from "pages/MasterPage1Column";
import TextBox from "components/TextBox";
import UserAvatar from "components/UserAvatar";
import { UserTypes } from "../../../api/auth";

interface OnlineDeviceRow {
  label?: string | null;
  browser?: string | null;
  os?: string | null;
  device?: string | null;
  count: number;
}

interface OnlineUserRow {
  userId: number;
  userType: UserTypes;
  userTypeText?: string | null;
  displayName2?: string | null;
  companyName?: string | null;
  divisionName?: string | null;
  departmentName?: string | null;
  positionName?: string | null;
  profileImage?: string | null;
  connectionCount: number;
  deviceCount: number;
  devices: OnlineDeviceRow[];
}

// ห้องแชท (Group/GroupSupplier) ไม่ใช่คนจริง จึงไม่ต้องมี tab
const TAB_USER_TYPES: UserTypes[] = [
  UserTypes.Employee,
  UserTypes.Customer,
  UserTypes.Supplier,
  UserTypes.ExternalUser,
];

const INTERVAL_OPTIONS = [
  { value: 0, text: "NONE" },
  { value: 1, text: "1 วินาที" },
  { value: 5, text: "5 วินาที" },
  { value: 10, text: "10 วินาที" },
];

interface MainComponentProps {
  onUserCountChanged(userCount: number): void;
}

const MainComponent = ({ onUserCountChanged }: MainComponentProps) => {

  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<OnlineUserRow[]>([]);
  const [keyword, setKeyword] = useState<string>("");
  const [intervalSec, setIntervalSec] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<string>(String(UserTypes.Employee));

  // fnLoad ถูกเรียกจาก setInterval ด้วย จึงเก็บไว้ใน ref กัน closure ค้างค่าเก่า
  const fnLoadRef = useRef<() => void>(() => { });

  const fnLoad = async (showLoader: boolean) => {
    if (showLoader) setLoading(true);
    new APIClient().fnPostForm(urls.CHAT_GET_ONLINE_USER, {})
      .then(rs => {
        setData(rs ?? []);
        setLoading(false);
      })
      .catch(er => {
        Utils.HandleException(er, true);
        setLoading(false);
      });
  }

  useEffect(() => {
    fnLoad(true);
  }, []);

  useEffect(() => {
    fnLoadRef.current = () => fnLoad(false); // auto reload ไม่ต้องขึ้น loader จะได้ไม่กระพริบ
  });

  useEffect(() => {
    if (intervalSec <= 0) return;
    const timer = setInterval(() => fnLoadRef.current(), intervalSec * 1000);
    return () => clearInterval(timer);
  }, [intervalSec]);

  useEffect(() => {
    onUserCountChanged((data ?? []).length);
  }, [data]);

  // live filter จาก data ที่โหลดมาแล้ว - ทำงานทั้งตอนพิมพ์ และตอน reload จาก backend
  const filtered = useMemo(() => {
    const kw = (keyword ?? "").trim().toLowerCase();
    if (kw === "") return data;
    return data.filter(u => (u.displayName2 ?? "").toLowerCase().includes(kw));
  }, [data, keyword]);

  const byUserType = useMemo(() => {
    const map = new Map<UserTypes, OnlineUserRow[]>();
    TAB_USER_TYPES.forEach(ut => map.set(ut, []));
    filtered.forEach(u => {
      const lst = map.get(u.userType);
      if (lst) lst.push(u); // userType ที่ไม่มี tab (ห้องแชท) ถูกตัดทิ้งตรงนี้
    });
    map.forEach(lst => lst.sort((a, b) => (a.displayName2 ?? "").localeCompare(b.displayName2 ?? "")));
    return map;
  }, [filtered]);

  const UserRow = (usr: OnlineUserRow, key: number) => {
    const subText = [usr.positionName, usr.departmentName, usr.divisionName, usr.companyName]
      .filter(x => (x ?? "").trim() !== "")
      .join(" / ");
    return (
      <div key={key} className="d-flex align-items-center hover line-divider-bottom p-1">
        <UserAvatar
          profileImage={usr.profileImage ?? ""}
          userId={usr.userId}
        />
        <div className="flex-grow-1 overflow-hidden">
          <div className="text-truncate">{usr.displayName2}</div>
          <div className="text-truncate fz-09 text-muted">{subText}</div>
        </div>

        {/* เครื่องที่ออนไลน์อยู่ */}
        <div className="ms-auto flex-shrink-0 text-end" style={{ maxWidth: "45%" }}>
          {(usr.devices ?? []).map((dev, k) => (
            <span
              key={k}
              className="badge fz-08 fw-normal rounded p-1 bg-secondary ms-1"
              title={[dev.browser, dev.os, dev.device].filter(x => (x ?? "").trim() !== "").join(" / ")}
            >
              {dev.label}{dev.count > 1 ? ` x${dev.count}` : ""}
            </span>
          ))}
        </div>

        {/* จำนวนเครื่องที่ออนไลน์ */}
        <div
          className="flex-shrink-0 text-center ms-2"
          style={{ width: "34px" }}
          title={`${usr.deviceCount} เครื่อง / ${usr.connectionCount} connection`}
        >
          <span className="badge fz-08 fw-normal rounded p-1 bg-primary">{usr.deviceCount}</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between" style={{ gap: "10px" }}>
        <div className="row" style={{ flexGrow: "1" }}>
          <div className="col">
            <TextBox
              placeholder="ค้นหาชื่อ"
              style={{ padding: "5px" }}
              icon13css="fas fa-search fz-10 me-1"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
          </div>
        </div>
        <div className="flex-shrink-0">
          <select
            className="form-select"
            style={{ padding: "5px" }}
            title="รอบเวลาโหลดข้อมูลใหม่"
            value={intervalSec}
            onChange={(e) => setIntervalSec(Number(e.target.value) || 0)}
          >
            {INTERVAL_OPTIONS.map((opt, key) => (
              <option key={key} value={opt.value}>{opt.text}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ height: "5px" }}></div>
      {loading && (<Loader />)}

      <Tabs
        id="online-user-tabs"
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k ?? String(UserTypes.Employee))}
      >
        {TAB_USER_TYPES.map((ut) => {
          const lst = byUserType.get(ut) ?? [];
          return (
            <Tab key={ut} eventKey={String(ut)} title={`${Utils.GetUserTypeText(ut)} (${lst.length})`}>
              <div style={{ height: "calc(100vh - 175px)", overflow: "auto" }}>
                {!loading && lst.length <= 0 && (<div className="text-center p-2">ไม่มีข้อมูล</div>)}
                {lst.map((usr, key) => UserRow(usr, key))}
              </div>
            </Tab>
          );
        })}
      </Tabs>
    </div>
  );
}

const DataSettingOnlineUser = () => {
  const onSetEditCol2Key = (key: any) => {
  }

  const [userCount, setUserCount] = useState<number>(0);

  const onUserCountChanged = (userCount: number) => {
    setUserCount(userCount);
  };

  return (
    <MasterPage1Column
      titleText={"ONLINE USER" + (userCount <= 0 ? "" : (` ${userCount} คน`))}
      titleIcon="fas fa-user-clock"
      onSetEditCol2Key={onSetEditCol2Key}
      componentCol1={<MainComponent onUserCountChanged={onUserCountChanged} />}
    ></MasterPage1Column>
  );
}

export default DataSettingOnlineUser;
